/**
 * Shared helpers for conversational-mode field sequence building.
 * Imported by both the live public-form-viewer and PreviewModal so that
 * visibility evaluation, state merging, and question ordering stay identical.
 */

import {
  buildNameValueMap,
  computeEffectiveFieldState,
  computeFieldState,
  computeReachablePageIds,
  evaluateFieldLogicRules,
  evaluateOperator,
  evaluatePageLogicRules,
  resolveNextPageIndex,
} from '../../shared/formLogic.js';

export {
  buildNameValueMap,
  computeEffectiveFieldState,
  computeFieldState,
  computeReachablePageIds,
  evaluateFieldLogicRules,
  evaluateOperator,
  evaluatePageLogicRules,
  resolveNextPageIndex,
};

export const CONV_LAYOUT_TYPES = new Set([
  'heading', 'paragraph', 'divider', 'media', 'content', 'layout', 'button',
]);

export const buildPageScopedFormValues = (steps, pageIds, formValues) => {
  const scopedValues = {};
  const allowedPageIds = pageIds instanceof Set ? pageIds : new Set(pageIds || []);
  for (const page of (steps || [])) {
    if (!allowedPageIds.has(page?.id)) continue;
    for (const fieldId of (page?.fieldIds || [])) {
      if (Object.prototype.hasOwnProperty.call(formValues || {}, fieldId)) {
        scopedValues[fieldId] = formValues[fieldId];
      }
    }
  }
  return scopedValues;
};

export const getConversationalScreenPageIndex = (screen, steps) => {
  const fieldId = screen?.field?.id || screen?.field?.name;
  if (!fieldId) return -1;
  return (steps || []).findIndex(step => (step?.fieldIds || []).includes(fieldId));
};

// Conversational mode shows one field at a time, but page actions must still
// fire at page boundaries. This maps the shared page result to the first visible
// question on the resolved destination page.
export const resolveNextConversationalScreenIndex = (
  questionScreens,
  currentScreenIndex,
  steps,
  pageLogicResult,
) => {
  const screens = Array.isArray(questionScreens) ? questionScreens : [];
  const pages = Array.isArray(steps) ? steps : [];
  const lastScreenIndex = screens.length - 1;
  if (currentScreenIndex >= lastScreenIndex) return currentScreenIndex;

  const sequentialIndex = currentScreenIndex + 1;
  const currentPageIndex = getConversationalScreenPageIndex(screens[currentScreenIndex], pages);
  const sequentialPageIndex = getConversationalScreenPageIndex(screens[sequentialIndex], pages);

  // Stay sequential while there are still visible questions on this page.
  if (currentPageIndex < 0 || sequentialPageIndex === currentPageIndex) {
    return sequentialIndex;
  }

  const destinationPageIndex = resolveNextPageIndex(
    pages,
    currentPageIndex,
    pageLogicResult,
  );
  const skippedPageIds = pageLogicResult?.skippedPageIds || new Set();

  for (let pageIndex = destinationPageIndex; pageIndex < pages.length; pageIndex += 1) {
    if (pageIndex < pages.length - 1 && skippedPageIds.has(pages[pageIndex]?.id)) continue;
    const destinationScreenIndex = screens.findIndex(
      (screen, screenIndex) =>
        screenIndex > currentScreenIndex &&
        getConversationalScreenPageIndex(screen, pages) === pageIndex,
    );
    if (destinationScreenIndex >= 0) return destinationScreenIndex;
  }

  // The resolved destination and every later page contain no visible questions.
  // Returning null lets the caller finish safely rather than falling backward to
  // a screen on a page that the rule explicitly skipped or jumped over.
  return null;
};

// ---------------------------------------------------------------------------
// Build a name→value map so dependency rules can look up sibling field values
// by either field.name or field.id (whichever the dependency references).
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Compute base visible/required/disabled/depError state from per-field config
// (conditionalVisibility + cross-field dependency rules).
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Merge base field state with logic-rule overrides (logicRules take precedence
// for properties they explicitly set). Pass logicOverrides=null/undefined when
// the context does not run the full logic engine (e.g. PreviewModal) — the
// per-field dependency rules in computeFieldState still apply.
// ---------------------------------------------------------------------------
// ---------------------------------------------------------------------------
// Build the ordered question-screen sequence for conversational mode.
//
// Returns an array of screen objects: { field, state, prelude }
//   field   — the interactive field for this screen (null for layout-only trailing screens)
//   state   — computeEffectiveFieldState result for `field`
//   prelude — array of { field, state } for non-interactive (layout) elements shown above
//
// logicOverrides: output of evaluateLogicRules (pass null in preview contexts that
// don't run the full logic engine — per-field dependencies still apply).
// ---------------------------------------------------------------------------
export const buildConvQuestionScreens = (allFields, nameValueMap, multiStepConfig, logicOverrides) => {
  const convSteps = multiStepConfig?.steps || [];

  // Step-ordered list: respect step assignment order, then append unassigned fields
  const stepOrderedFields = [];
  const seenIds = new Set();
  for (const step of convSteps) {
    for (const fid of (step?.fieldIds || [])) {
      const f = allFields.find(f => f?.id === fid);
      if (f && !seenIds.has(fid)) { stepOrderedFields.push(f); seenIds.add(fid); }
    }
  }
  for (const f of allFields) {
    if (!seenIds.has(f?.id)) { stepOrderedFields.push(f); seenIds.add(f?.id); }
  }

  // Group into screens: layout/display types accumulate as a prelude for the next
  // interactive field; each interactive field gets its own screen.
  const questionScreens = [];
  let pendingLayout = [];
  for (const field of stepOrderedFields) {
    if (field?.type === 'hidden') continue;
    const fState = computeEffectiveFieldState(field, nameValueMap, logicOverrides);
    if (!fState.visible) continue;
    if (CONV_LAYOUT_TYPES.has(field?.type)) {
      pendingLayout.push({ field, state: fState });
    } else {
      questionScreens.push({ field, state: fState, prelude: pendingLayout });
      pendingLayout = [];
    }
  }
  // Trailing layout-only group
  if (pendingLayout.length > 0) {
    questionScreens.push({ field: null, state: null, prelude: pendingLayout });
  }

  return questionScreens;
};
